import { useEffect, useState } from "react";

/**
 * Which layout a screen should get. Deliberately three tiers rather than a
 * mobile/desktop boolean: tablets can carry the desktop composition but not
 * its fixed-viewport height model, and the two need to be decided separately.
 */
export type DeviceTier = "mobile" | "tablet" | "desktop";

/**
 * Breakpoints are declared once in tokens.css and read back here, so a change
 * there moves the CSS and the JS together. Media queries can't reference a
 * custom property, so the values are mirrored as literals in the stylesheets —
 * reading them at runtime is what keeps the two honest.
 */
const FALLBACK = { md: 768, lg: 1024 };

function readBreakpoints(): { md: number; lg: number } {
  if (typeof window === "undefined") return FALLBACK;
  try {
    const root = getComputedStyle(document.documentElement);
    const parse = (name: string, fallback: number): number => {
      const raw = root.getPropertyValue(name).trim();
      const value = Number.parseInt(raw, 10);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };
    return { md: parse("--bp-md", FALLBACK.md), lg: parse("--bp-lg", FALLBACK.lg) };
  } catch {
    return FALLBACK;
  }
}

function resolveTier(): DeviceTier {
  if (typeof window === "undefined") return "desktop";
  const { md, lg } = readBreakpoints();
  const width = window.innerWidth;
  if (width < md) return "mobile";
  if (width < lg) return "tablet";
  return "desktop";
}

/**
 * The active layout tier, kept in step with viewport changes.
 *
 * Prefer a CSS media query where the difference is purely visual — this hook
 * is for cases where the *markup* differs (rendering a bottom tab bar, or
 * choosing the calendar's default view), because those can't be expressed in
 * CSS and shouldn't ship both trees to every device.
 */
export function useDeviceTier(): DeviceTier {
  const [tier, setTier] = useState<DeviceTier>(resolveTier);

  useEffect(() => {
    const { md, lg } = readBreakpoints();
    // Listening to the same boundaries the CSS uses means this only fires when
    // a tier actually changes, not on every pixel of a window drag.
    const queries = [
      window.matchMedia(`(max-width: ${md - 1}px)`),
      window.matchMedia(`(min-width: ${md}px) and (max-width: ${lg - 1}px)`),
    ];
    const sync = () => setTier(resolveTier());
    queries.forEach((q) => q.addEventListener("change", sync));
    sync();
    return () => queries.forEach((q) => q.removeEventListener("change", sync));
  }, []);

  return tier;
}

/** Convenience for the common "is this the phone layout?" check. */
export function useIsMobile(): boolean {
  return useDeviceTier() === "mobile";
}
